import React, { useEffect, useState } from "react";
import {
  useTranslate,
  useCustom,
  useCustomMutation,
  useApiUrl,
} from "@refinedev/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../../authProvider";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  LinkOutlined,
  DisconnectOutlined,
  ImportOutlined,
  SyncOutlined,
  ReloadOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";

const { Text, Paragraph } = Typography;

interface IQontoConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  scopes?: string;
}

interface IQontoImportPreview {
  totalQontoClients: number;
  toCreate: number;
  toUpdate: number;
  skippedNoTin: number;
  skippedIndividual: number;
}

interface IQontoPushPreview {
  totalCompanyAccounts: number;
  toSync: number;
  skippedNoLegalId: number;
}

interface IQontoImportResult {
  created: number;
  updated: number;
  skippedNoTin: number;
  skippedIndividual: number;
  errors: { legalId?: string; message: string }[];
}

interface IQontoSyncErrorItem {
  accountId?: number;
  accountName?: string;
  legalId?: string;
  message: string;
}

interface IQontoBankAccount {
  id: string;
  name?: string;
  iban?: string;
  bic?: string;
  currency?: string;
  status?: string;
  main?: boolean;
}

interface IQontoOrganizationAddress {
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

interface IQontoOrganization {
  legalName?: string;
  legalNumber?: string;
  nafCode?: string;
  address?: IQontoOrganizationAddress;
  bankAccounts: IQontoBankAccount[];
}

interface QontoIntegrationCardProps {
  /** Current selected payment engine in the invoice settings form. */
  paymentEngine?: string;
  /** Current selected invoicing engine in the invoice settings form. */
  invoicingEngine?: string;
  /** Called after invoice settings are filled from Qonto so the parent can refetch. */
  onImported?: () => void;
}

interface IQontoPushResult {
  synced: number;
  failed: number;
  skipped: number;
  errors: IQontoSyncErrorItem[];
}

export const QontoIntegrationCard: React.FC<QontoIntegrationCardProps> = ({
  paymentEngine,
  invoicingEngine,
  onImported,
}) => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [importPreview, setImportPreview] =
    useState<IQontoImportPreview | null>(null);
  const [pushPreview, setPushPreview] = useState<IQontoPushPreview | null>(
    null,
  );
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isPushModalOpen, setIsPushModalOpen] = useState(false);
  const [importBillingMode, setImportBillingMode] = useState<
    "prepaid" | "postpaid"
  >("postpaid");
  const [isPushErrorsModalOpen, setIsPushErrorsModalOpen] = useState(false);
  const [pushSyncErrors, setPushSyncErrors] = useState<IQontoSyncErrorItem[]>(
    [],
  );
  const [pushSyncSummary, setPushSyncSummary] = useState<{
    synced: number;
    failed: number;
  } | null>(null);
  const [isImportingDetails, setIsImportingDetails] = useState(false);
  const [isBankSelectModalOpen, setIsBankSelectModalOpen] = useState(false);
  const [pendingOrg, setPendingOrg] = useState<IQontoOrganization | null>(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<
    string | undefined
  >(undefined);

  const unwrapMutationData = <T,>(response: unknown): T => {
    const wrapped = response as { data?: T };
    return (wrapped?.data ?? response) as T;
  };

  const {
    data: statusResponse,
    isLoading,
    isError,
    refetch,
  } = useCustom<IQontoConnectionStatus>({
    url: "integrations/qonto",
    method: "get",
    queryOptions: {
      retry: false,
    },
  });

  const { mutate: disconnectQonto, isLoading: isDisconnecting } =
    useCustomMutation();

  const { mutate: importClients, isLoading: isImporting } =
    useCustomMutation<IQontoImportResult>();

  const { mutate: syncAccounts, isLoading: isSyncing } =
    useCustomMutation<IQontoPushResult>();

  const { mutate: saveInvoiceSettings, isLoading: isSavingSettings } =
    useCustomMutation();

  const status = statusResponse?.data;
  const isConnected = status?.connected === true;
  const isPaymentEngine = paymentEngine === "qonto";
  const isInvoicingEngine = invoicingEngine === "qonto";

  useEffect(() => {
    const qontoResult = searchParams.get("qonto");
    if (!qontoResult) {
      return;
    }
    const reason = searchParams.get("reason");
    if (qontoResult === "connected") {
      message.success(
        translate(
          "invoice-settings.integrations.qonto.connectedSuccess",
          "Compte Qonto connecté avec succès",
        ),
      );
      void refetch();
    } else if (qontoResult === "error") {
      message.error(
        translate("invoice-settings.integrations.qonto.connectedError", {
          reason: reason ?? translate("common.unknown", "inconnu"),
          defaultValue: `Échec de la connexion Qonto : ${reason ?? "inconnu"}`,
        }),
      );
    }
    navigate("/configuration/organization?tab=integrations", { replace: true });
  }, [searchParams, navigate, refetch, translate]);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const response = await axiosInstance.get<{ authorizationUrl: string }>(
        `${apiUrl}/integrations/qonto/authorize`,
      );
      const authorizationUrl = response.data?.authorizationUrl;
      if (!authorizationUrl) {
        throw new Error("missing_authorization_url");
      }
      window.location.href = authorizationUrl;
    } catch {
      message.error(
        translate(
          "invoice-settings.integrations.qonto.connectError",
          "Impossible de démarrer la connexion Qonto",
        ),
      );
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    disconnectQonto(
      {
        url: "integrations/qonto",
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success(
            translate(
              "invoice-settings.integrations.qonto.disconnectedSuccess",
              "Compte Qonto déconnecté",
            ),
          );
          setIsManageModalOpen(false);
          void refetch();
        },
        onError: () => {
          message.error(
            translate(
              "invoice-settings.integrations.qonto.disconnectError",
              "Impossible de déconnecter Qonto",
            ),
          );
        },
      },
    );
  };

  const openImportModal = async () => {
    try {
      const response = await axiosInstance.get<IQontoImportPreview>(
        `${apiUrl}/integrations/qonto/import-clients/preview`,
      );
      setImportPreview(response.data);
      setImportBillingMode("postpaid");
      setIsImportModalOpen(true);
    } catch {
      message.error(
        translate(
          "invoice-settings.integrations.qonto.importPreviewError",
          "Impossible de prévisualiser l'import Qonto",
        ),
      );
    }
  };

  const openPushModal = async () => {
    try {
      const response = await axiosInstance.get<IQontoPushPreview>(
        `${apiUrl}/integrations/qonto/sync-accounts/preview`,
      );
      setPushPreview(response.data);
      setIsPushModalOpen(true);
    } catch {
      message.error(
        translate(
          "invoice-settings.integrations.qonto.syncPreviewError",
          "Impossible de prévisualiser la synchronisation",
        ),
      );
    }
  };

  const handleConfirmImport = () => {
    importClients(
      {
        url: "integrations/qonto/import-clients",
        method: "post",
        values: { confirmed: true, billingMode: importBillingMode },
      },
      {
        onSuccess: (response) => {
          const result = unwrapMutationData<IQontoImportResult>(response);
          const created = result?.created ?? 0;
          const updated = result?.updated ?? 0;
          setIsImportModalOpen(false);
          message.success(
            translate("invoice-settings.integrations.qonto.importSuccess", {
              created,
              updated,
              defaultValue: `Import terminé : ${created} créés, ${updated} mis à jour`,
            }),
          );
        },
        onError: () => {
          message.error(
            translate(
              "invoice-settings.integrations.qonto.importError",
              "Échec de l'import Qonto",
            ),
          );
        },
      },
    );
  };

  const handleConfirmPush = () => {
    syncAccounts(
      {
        url: "integrations/qonto/sync-accounts",
        method: "post",
        values: { confirmed: true },
      },
      {
        onSuccess: (response) => {
          const result = unwrapMutationData<IQontoPushResult>(response);
          const synced = result?.synced ?? 0;
          const failed = result?.failed ?? 0;
          const errors = result?.errors ?? [];
          setIsPushModalOpen(false);
          if (failed > 0 && errors.length > 0) {
            setPushSyncErrors(errors);
            setPushSyncSummary({ synced, failed });
            setIsPushErrorsModalOpen(true);
            message.warning(
              translate("invoice-settings.integrations.qonto.syncPartial", {
                synced,
                failed,
                defaultValue: `Synchronisation terminée avec ${failed} échec(s) sur ${synced + failed} compte(s)`,
              }),
            );
          } else {
            message.success(
              translate("invoice-settings.integrations.qonto.syncSuccess", {
                synced,
                failed,
                defaultValue: `Synchronisation terminée : ${synced} réussis, ${failed} échecs`,
              }),
            );
          }
        },
        onError: () => {
          message.error(
            translate(
              "invoice-settings.integrations.qonto.syncError",
              "Échec de la synchronisation vers Qonto",
            ),
          );
        },
      },
    );
  };

  const applyOrgToSettings = (
    org: IQontoOrganization,
    account: IQontoBankAccount,
  ) => {
    if (!org.address) {
      message.error(
        translate(
          "invoice-settings.integrations.qonto.importDetailsNoAddress",
          "Aucune adresse légale trouvée dans Qonto. Complétez l'adresse manuellement.",
        ),
      );
      return;
    }

    const payload: {
      companyName?: string;
      siren?: string;
      iban?: string;
      bic?: string;
      nafCode?: string;
      address?: IQontoOrganizationAddress;
    } = {};

    if (org.legalName) {
      payload.companyName = org.legalName;
    }
    // Qonto's legal_number may be a 14-digit SIRET (or formatted). The siren
    // field expects the 9-digit SIREN, which is the first 9 digits of a SIRET.
    const sirenDigits = (org.legalNumber ?? "").replace(/\D/g, "");
    if (sirenDigits.length >= 9) {
      payload.siren = sirenDigits.slice(0, 9);
    }
    if (org.nafCode) {
      payload.nafCode = org.nafCode;
    }
    if (account.iban) {
      payload.iban = account.iban;
    }
    if (account.bic) {
      payload.bic = account.bic;
    }

    const addressFields: IQontoOrganizationAddress = {};
    if (org.address.street) {
      addressFields.street = org.address.street;
    }
    if (org.address.city) {
      addressFields.city = org.address.city;
    }
    if (org.address.postalCode) {
      addressFields.postalCode = org.address.postalCode;
    }
    if (org.address.country) {
      addressFields.country = org.address.country;
    }
    if (Object.keys(addressFields).length > 0) {
      payload.address = addressFields;
    }

    saveInvoiceSettings(
      {
        url: "invoice-settings",
        method: "patch",
        values: payload,
      },
      {
        onSuccess: () => {
          setIsBankSelectModalOpen(false);
          setPendingOrg(null);
          message.success(
            translate(
              "invoice-settings.integrations.qonto.importDetailsSuccess",
              "Paramètres de facturation importés depuis Qonto",
            ),
          );
          onImported?.();
        },
        onError: () => {
          message.error(
            translate(
              "invoice-settings.integrations.qonto.importDetailsError",
              "Échec de l'import des paramètres depuis Qonto",
            ),
          );
        },
      },
    );
  };

  const handleImportDetails = async () => {
    try {
      setIsImportingDetails(true);
      const response = await axiosInstance.get<IQontoOrganization>(
        `${apiUrl}/integrations/qonto/organization`,
      );
      const org = response.data;
      const activeAccounts = (org.bankAccounts ?? []).filter(
        (account) => account.status === "active",
      );

      if (activeAccounts.length === 0) {
        message.error(
          translate(
            "invoice-settings.integrations.qonto.noBankAccounts",
            "Aucun compte bancaire actif trouvé dans Qonto",
          ),
        );
        return;
      }

      if (activeAccounts.length === 1) {
        applyOrgToSettings(org, activeAccounts[0]);
        return;
      }

      const defaultAccount =
        activeAccounts.find((account) => account.main) ?? activeAccounts[0];
      setPendingOrg({ ...org, bankAccounts: activeAccounts });
      setSelectedBankAccountId(defaultAccount.id);
      setIsBankSelectModalOpen(true);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) {
        message.error(
          translate(
            "invoice-settings.integrations.qonto.reconnectRequired",
            "Reconnectez Qonto pour autoriser l'accès aux informations de l'organisation.",
          ),
        );
      } else {
        message.error(
          translate(
            "invoice-settings.integrations.qonto.importDetailsError",
            "Échec de l'import des paramètres depuis Qonto",
          ),
        );
      }
    } finally {
      setIsImportingDetails(false);
    }
  };

  const handleConfirmBankSelection = () => {
    if (!pendingOrg) {
      return;
    }
    const account = pendingOrg.bankAccounts.find(
      (item) => item.id === selectedBankAccountId,
    );
    if (!account) {
      return;
    }
    applyOrgToSettings(pendingOrg, account);
  };

  if (isLoading) {
    return (
      <Card className="mb-4">
        <Spin />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="mb-4">
        <Alert
          type="warning"
          showIcon
          message={translate(
            "invoice-settings.integrations.qonto.unavailable",
            "Intégration Qonto indisponible",
          )}
          description={translate(
            "invoice-settings.integrations.qonto.unavailableHint",
            "L'intégration n'est pas activée ou n'est pas configurée sur le serveur.",
          )}
        />
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4 !border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Space wrap>
            <Text strong>
              {translate("invoice-settings.integrations.qonto.title", "Qonto")}
            </Text>
            {isConnected ? (
              <Tag color="success">
                {translate(
                  "invoice-settings.integrations.qonto.connected",
                  "Connecté",
                )}
              </Tag>
            ) : (
              <Tag>
                {translate(
                  "invoice-settings.integrations.collapsed.notConnected",
                  "Non connecté",
                )}
              </Tag>
            )}
            {isPaymentEngine && (
              <Tag color="blue">
                {translate(
                  "invoice-settings.engines.badgePayment",
                  "Paiement",
                )}
              </Tag>
            )}
            {isInvoicingEngine && (
              <Tag color="blue">
                {translate(
                  "invoice-settings.engines.badgeInvoicing",
                  "Facturation",
                )}
              </Tag>
            )}
          </Space>
          {isConnected ? (
            <Button type="primary" onClick={() => setIsManageModalOpen(true)}>
              {translate(
                "invoice-settings.integrations.collapsed.manage",
                "Gérer",
              )}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<LinkOutlined />}
              loading={isConnecting}
              onClick={() => {
                void handleConnect();
              }}
            >
              {translate(
                "invoice-settings.integrations.qonto.connect",
                "Se connecter à Qonto",
              )}
            </Button>
          )}
        </div>
      </Card>

      <Modal
        title={translate(
          "invoice-settings.integrations.qonto.modalTitle",
          "Gérer Qonto",
        )}
        open={isManageModalOpen}
        onCancel={() => setIsManageModalOpen(false)}
        footer={null}
        destroyOnClose
        width={720}
      >
        <Space direction="vertical" size="middle" className="w-full">
          <Paragraph type="secondary" className="!mb-0">
            {translate(
              "invoice-settings.integrations.qonto.description",
              "Connectez votre compte Qonto pour utiliser l'API bancaire depuis cette plateforme.",
            )}
          </Paragraph>

          <Tag color="success">
            {translate(
              "invoice-settings.integrations.qonto.connected",
              "Connecté",
            )}
          </Tag>
          <Descriptions column={1} size="small" bordered>
            {status?.connectedAt && (
              <Descriptions.Item
                label={translate(
                  "invoice-settings.integrations.qonto.connectedAt",
                  "Connecté le",
                )}
              >
                {new Date(status.connectedAt).toLocaleString("fr-FR")}
              </Descriptions.Item>
            )}
            {status?.scopes && (
              <Descriptions.Item
                label={translate(
                  "invoice-settings.integrations.qonto.scopes",
                  "Autorisations",
                )}
              >
                {status.scopes}
              </Descriptions.Item>
            )}
          </Descriptions>
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleConnect}
              loading={isConnecting}
            >
              {translate(
                "invoice-settings.integrations.qonto.reconnect",
                "Reconnecter",
              )}
            </Button>
            <Button
              danger
              icon={<DisconnectOutlined />}
              onClick={handleDisconnect}
              loading={isDisconnecting}
            >
              {translate(
                "invoice-settings.integrations.qonto.disconnect",
                "Déconnecter",
              )}
            </Button>
          </Space>

          {invoicingEngine === "qonto" && (
            <>
              <Divider className="!my-2" />
              <Typography.Title level={5} className="!mt-0">
                {translate(
                  "invoice-settings.integrations.qonto.importDetailsTitle",
                  "Paramètres de facturation",
                )}
              </Typography.Title>
              <Paragraph type="secondary" className="!mt-0">
                {translate(
                  "invoice-settings.integrations.qonto.importDetailsHint",
                  "Importez le nom légal, l'adresse, le code NAF, le SIREN et l'IBAN/BIC depuis votre organisation Qonto.",
                )}
              </Paragraph>
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                onClick={handleImportDetails}
                loading={isImportingDetails || isSavingSettings}
              >
                {translate(
                  "invoice-settings.integrations.qonto.importDetails",
                  "Importer depuis Qonto",
                )}
              </Button>
            </>
          )}

          <Divider className="!my-2" />

          <Typography.Title level={5} className="!mt-0">
            {translate(
              "invoice-settings.integrations.qonto.bulkActions",
              "Actions en masse",
            )}
          </Typography.Title>
          <Space wrap>
            <Button icon={<ImportOutlined />} onClick={openImportModal}>
              {translate(
                "invoice-settings.integrations.qonto.importClients",
                "Importer les clients Qonto",
              )}
            </Button>
            <Button icon={<SyncOutlined />} onClick={openPushModal}>
              {translate(
                "invoice-settings.integrations.qonto.syncAccounts",
                "Synchroniser les comptes vers Qonto",
              )}
            </Button>
          </Space>
        </Space>
      </Modal>

      <Modal
        title={translate(
          "invoice-settings.integrations.qonto.importClients",
          "Importer les clients Qonto",
        )}
        open={isImportModalOpen}
        onCancel={() => setIsImportModalOpen(false)}
        onOk={handleConfirmImport}
        okText={translate("buttons.confirm", "Confirmer")}
        cancelText={translate("buttons.cancel", "Annuler")}
        confirmLoading={isImporting}
      >
        <Paragraph>
          {translate(
            "invoice-settings.integrations.qonto.importConfirm",
            "Les données Qonto écraseront les comptes locaux existants lorsque le SIRET/SIREN correspond. Continuer ?",
          )}
        </Paragraph>
        <Form layout="vertical" className="!mb-4">
          <Form.Item
            label={translate(
              "invoice-settings.integrations.qonto.importBillingMode",
              "Mode de facturation des comptes importés",
            )}
          >
            <Select
              value={importBillingMode}
              onChange={setImportBillingMode}
              options={[
                {
                  value: "postpaid",
                  label: translate(
                    "accounts.fields.billingModeValues.postpaid",
                    "Postpayé",
                  ),
                },
                {
                  value: "prepaid",
                  label: translate(
                    "accounts.fields.billingModeValues.prepaid",
                    "Prépayé",
                  ),
                },
              ]}
            />
          </Form.Item>
        </Form>
        {importPreview && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Total clients Qonto">
              {importPreview.totalQontoClients}
            </Descriptions.Item>
            <Descriptions.Item label={translate("common.create", "Créer")}>
              {importPreview.toCreate}
            </Descriptions.Item>
            <Descriptions.Item
              label={translate("common.update", "Mettre à jour")}
            >
              {importPreview.toUpdate}
            </Descriptions.Item>
            <Descriptions.Item
              label={translate(
                "invoice-settings.integrations.qonto.skippedNoTin",
                "Sans n° fiscal",
              )}
            >
              {importPreview.skippedNoTin}
            </Descriptions.Item>
            <Descriptions.Item
              label={translate(
                "invoice-settings.integrations.qonto.skippedIndividual",
                "Particuliers ignorés",
              )}
            >
              {importPreview.skippedIndividual}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={translate(
          "invoice-settings.integrations.qonto.syncAccounts",
          "Synchroniser les comptes vers Qonto",
        )}
        open={isPushModalOpen}
        onCancel={() => setIsPushModalOpen(false)}
        onOk={handleConfirmPush}
        okText={translate("buttons.confirm", "Confirmer")}
        cancelText={translate("buttons.cancel", "Annuler")}
        confirmLoading={isSyncing}
      >
        <Paragraph>
          {translate(
            "invoice-settings.integrations.qonto.syncConfirm",
            "Les comptes société seront créés ou mis à jour dans Qonto. Continuer ?",
          )}
        </Paragraph>
        {pushPreview && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item
              label={translate(
                "invoice-settings.integrations.qonto.companyAccounts",
                "Comptes société",
              )}
            >
              {pushPreview.totalCompanyAccounts}
            </Descriptions.Item>
            <Descriptions.Item
              label={translate("common.sync", "À synchroniser")}
            >
              {pushPreview.toSync}
            </Descriptions.Item>
            <Descriptions.Item
              label={translate(
                "invoice-settings.integrations.qonto.skippedNoLegalId",
                "Sans SIRET",
              )}
            >
              {pushPreview.skippedNoLegalId}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={translate(
          "invoice-settings.integrations.qonto.syncErrorsTitle",
          "Échecs de synchronisation vers Qonto",
        )}
        open={isPushErrorsModalOpen}
        onCancel={() => {
          setIsPushErrorsModalOpen(false);
          setPushSyncErrors([]);
          setPushSyncSummary(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setIsPushErrorsModalOpen(false);
              setPushSyncErrors([]);
              setPushSyncSummary(null);
            }}
          >
            {translate("buttons.close", "Fermer")}
          </Button>,
        ]}
        width={920}
      >
        {pushSyncSummary && (
          <Paragraph type="secondary" className="!mb-4">
            {translate("invoice-settings.integrations.qonto.syncErrorsSummary", {
              synced: pushSyncSummary.synced,
              failed: pushSyncSummary.failed,
              defaultValue: `${pushSyncSummary.synced} compte(s) synchronisé(s), ${pushSyncSummary.failed} en échec`,
            })}
          </Paragraph>
        )}
        <Table
          size="small"
          rowKey={(row) =>
            String(row.accountId ?? row.legalId ?? row.message)
          }
          dataSource={pushSyncErrors}
          pagination={false}
          scroll={{ y: 360 }}
          columns={[
            {
              title: translate("accounts.fields.name", "Nom"),
              dataIndex: "accountName",
              key: "accountName",
              render: (name: string | undefined, row: IQontoSyncErrorItem) =>
                name ?? `Compte #${row.accountId ?? "—"}`,
            },
            {
              title: translate("accounts.fields.legalId", "SIRET"),
              dataIndex: "legalId",
              key: "legalId",
              width: 140,
              render: (legalId: string | undefined) => legalId ?? "—",
            },
            {
              title: translate(
                "invoice-settings.integrations.qonto.syncErrorColumn",
                "Erreur",
              ),
              dataIndex: "message",
              key: "message",
              ellipsis: true,
            },
          ]}
        />
      </Modal>

      <Modal
        title={translate(
          "invoice-settings.integrations.qonto.selectBankAccountTitle",
          "Sélectionner le compte bancaire par défaut",
        )}
        open={isBankSelectModalOpen}
        onCancel={() => {
          setIsBankSelectModalOpen(false);
          setPendingOrg(null);
        }}
        onOk={handleConfirmBankSelection}
        okText={translate("buttons.confirm", "Confirmer")}
        cancelText={translate("buttons.cancel", "Annuler")}
        confirmLoading={isSavingSettings}
        okButtonProps={{ disabled: !selectedBankAccountId }}
      >
        <Paragraph>
          {translate(
            "invoice-settings.integrations.qonto.selectBankAccountHint",
            "Votre organisation Qonto possède plusieurs comptes bancaires. Choisissez celui à utiliser pour la facturation.",
          )}
        </Paragraph>
        <Select
          className="w-full"
          value={selectedBankAccountId}
          onChange={setSelectedBankAccountId}
          options={(pendingOrg?.bankAccounts ?? []).map((account) => ({
            value: account.id,
            label: `${account.name ?? translate("invoice-settings.integrations.qonto.bankAccount", "Compte")} — ${account.iban ?? ""}${account.main ? ` (${translate("invoice-settings.integrations.qonto.mainAccount", "principal")})` : ""}`,
          }))}
        />
      </Modal>
    </>
  );
};
